import React, { useEffect, useRef } from 'react';
import { Modal, Box, Typography, IconButton, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

let __scrollLocks = 0;
let __prev = {
  bodyOverflow: '',
  bodyPaddingRight: '',
  htmlOverflow: '',
};

function lockScroll() {
  __scrollLocks += 1;
  if (__scrollLocks !== 1) return;

  const body = document.body;
  const html = document.documentElement;

  __prev = {
    bodyOverflow: body.style.overflow,
    bodyPaddingRight: body.style.paddingRight,
    htmlOverflow: html.style.overflow,
  };

  const scrollbarWidth = window.innerWidth - html.clientWidth;
  if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

  html.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
}

function unlockScroll() {
  __scrollLocks = Math.max(0, __scrollLocks - 1);
  if (__scrollLocks !== 0) return;

  const body = document.body;
  const html = document.documentElement;

  body.style.overflow = __prev.bodyOverflow || '';
  body.style.paddingRight = __prev.bodyPaddingRight || '';
  html.style.overflow = __prev.htmlOverflow || '';
}

const h1Sx = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 600,
  fontSize: '42px',
  lineHeight: '100%',
  letterSpacing: 0,
  color: '#FFFFFF',
};

const h2Sx = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 500,
  fontSize: '28px',
  lineHeight: '50px',
  letterSpacing: 0,
  color: '#FFFFFF',
  mt: 4,
  mb: 1,
};

const pSx = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '26px',
  letterSpacing: 0,
  color: '#FFFFFF',
  opacity: 0.85,
};

const liSx = { ...pSx, mb: 1 };

const PrivacyPolicyModal = () => {
  const isMobile = useMediaQuery('(max-width:859px)');
  const navigate = useNavigate();
  const closeBtnRef = useRef(null);

  const handleClose = () => {
    navigate('/');
  };

  useEffect(() => {
    lockScroll();

    const id = requestAnimationFrame(() => {
      closeBtnRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(id);
      unlockScroll();
    };
  }, []);

  return (
    <Modal
      open
      onClose={handleClose}
      disableScrollLock
      disableAutoFocus
      disableRestoreFocus
      sx={{
        '& .MuiFocusTrap-sentinel:focus': { outline: 'none' },
        '& .MuiBackdrop-root': { outline: 'none' },
      }}
    >
      <Box sx={{ px: isMobile ? 1 : 0 }}>
        <Box
          sx={{
            width: '98%',
            maxWidth: isMobile ? '420px' : '980px',
            mx: 'auto',
            my: '8vh',
            borderRadius: '38px',
            position: 'relative',
            background: 'rgba(0,0,0,0.88)',
            backdropFilter: 'blur(20px)',
            boxShadow: 24,
            maxHeight: '84vh',
            overflow: 'hidden',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              display: 'flex',
              justifyContent: 'flex-end',
              p: isMobile ? 2 : 2.5,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0) 100%)',
              pointerEvents: 'auto',
            }}
          >
            <IconButton
              ref={closeBtnRef}
              onClick={handleClose}
              aria-label="Close privacy policy"
              sx={{
                color: '#FFFFFF',
                width: 44,
                height: 44,
                backgroundColor: 'rgba(255,255,255,0.08)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
                '&:focus': { outline: 'none' },
              }}
            >
              <CloseIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              px: isMobile ? 2.5 : 6,
              pt: isMobile ? 1 : 1.5,
              pb: isMobile ? 4 : 6,
            }}
          >
            <Typography sx={h1Sx}>AuraTap — Privacy Policy</Typography>

            <Typography sx={{ ...pSx, mt: 2 }}>
              <strong style={{ fontWeight: 600, color: 'inherit' }}>Effective Date:</strong> February 17, 2026
            </Typography>

            <Typography sx={{ ...pSx, mt: 2 }}>
              AuraTap is a social network designed for Apple Vision Pro. We respect your privacy and are committed to
              protecting the personal information you share with us. This policy explains what we collect, why, and how
              we handle it.
            </Typography>

            <Typography sx={h2Sx}>1. Information We Collect</Typography>

            <Typography sx={{ ...pSx, mt: 1 }}>
              <strong style={{ fontWeight: 600, color: 'inherit' }}>Account Information</strong>
              <br />
              When you create an AuraTap account, we collect your name, email address, and username. This information is
              necessary to identify you on the platform and allow others to find and connect with you.
            </Typography>

            <Typography sx={{ ...pSx, mt: 2 }}>
              <strong style={{ fontWeight: 600, color: 'inherit' }}>Profile Photos &amp; Avatars</strong>
              <br />
              You may upload a profile photo or avatar. This image is stored on our servers and displayed to other users
              within the app as part of your public profile.
            </Typography>

            <Typography sx={{ ...pSx, mt: 2 }}>
              <strong style={{ fontWeight: 600, color: 'inherit' }}>Camera &amp; Microphone Access</strong>
              <br />
              AuraTap requires access to your device&apos;s camera and microphone to enable your Persona on Apple Vision
              Pro. This allows the service to operate and provide you with video and audio calls with other users.
              Camera and microphone data is used solely for real-time communication and is not recorded or stored by
              AuraTap.
            </Typography>

            <Typography sx={{ ...pSx, mt: 2 }}>
              <strong style={{ fontWeight: 600, color: 'inherit' }}>Usage &amp; Analytics Data</strong>
              <br />
              We collect anonymized usage data such as app opens, feature interactions, and crash reports. This helps us
              understand how people use AuraTap and improve the experience. This data is not personally identifiable.
            </Typography>

            <Typography sx={h2Sx}>2. Information We Do Not Collect</Typography>
            <Typography sx={pSx}>
              AuraTap does not collect location data, contacts, health data, financial information, or any biometric data
              (including eye-tracking or hand-tracking data processed by Apple Vision Pro).
            </Typography>

            <Typography sx={h2Sx}>3. How We Use Your Information</Typography>
            <Typography sx={pSx}>We use the information we collect to:</Typography>

            <Box component="ul" sx={{ mt: 1.5, pl: 3, mb: 0 }}>
              <Typography component="li" sx={liSx}>
                Create and manage your account
              </Typography>
              <Typography component="li" sx={liSx}>
                Display your profile to other users on the platform
              </Typography>
              <Typography component="li" sx={liSx}>
                Enable Persona-based video and audio calls between users
              </Typography>
              <Typography component="li" sx={liSx}>
                Send you important service-related communications
              </Typography>
              <Typography component="li" sx={liSx}>
                Analyze app usage to improve features and fix issues
              </Typography>
              <Typography component="li" sx={liSx}>
                Ensure the security and integrity of the platform
              </Typography>
            </Box>

            <Typography sx={h2Sx}>4. Data Storage &amp; Third-Party Services</Typography>
            <Typography sx={pSx}>
              All user data is stored on our own servers and databases. We do not rely on third-party cloud platforms to
              host your personal information. We use industry-standard encryption and security practices to protect your
              data. No advertising networks or ad trackers are used in AuraTap.
            </Typography>

            <Typography sx={{ ...pSx, mt: 2 }}>
              We retain your personal information only for as long as your account is active or as needed to provide our
              services. You may request deletion of your account and associated data at any time.
            </Typography>

            <Typography sx={h2Sx}>5. Data Sharing</Typography>
            <Typography sx={pSx}>
              We do not sell, rent, or trade your personal information to third parties. We may share data only in the
              following circumstances:
            </Typography>

            <Box component="ul" sx={{ mt: 1.5, pl: 3, mb: 0 }}>
              <Typography component="li" sx={liSx}>
                With your consent — if you explicitly agree to share information.
              </Typography>
              <Typography component="li" sx={liSx}>
                Legal requirements — if required by law, regulation, or legal process.
              </Typography>
              <Typography component="li" sx={liSx}>
                Safety — to protect the rights, safety, or property of AuraTap, its users, or the public.
              </Typography>
            </Box>

            <Typography sx={h2Sx}>6. Your Rights</Typography>
            <Typography sx={pSx}>
              Depending on your location, you may have the right to access, correct, or delete your personal data, as
              well as the right to object to or restrict certain processing. To exercise any of these rights, please
              contact us at the email address below.
            </Typography>

            <Typography sx={h2Sx}>7. Children&apos;s Privacy</Typography>
            <Typography sx={pSx}>
              AuraTap is not intended for users under the age of 18. We do not knowingly collect personal information
              from minors. If we become aware that a user under 18 has provided us with personal data, we will take steps
              to delete that information promptly.
            </Typography>

            <Typography sx={h2Sx}>8. Changes to This Policy</Typography>
            <Typography sx={pSx}>
              We may update this privacy policy from time to time. When we do, we will revise the effective date at the
              top of this page and notify users through the app where appropriate.
            </Typography>

            <Typography sx={h2Sx}>9. Contact Us</Typography>
            <Typography sx={pSx}>
              If you have questions or concerns about this privacy policy or your personal data, please contact us at:
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography sx={pSx}>
                <strong style={{ fontWeight: 600, color: 'inherit' }}>Email:</strong> hello@auratap.app
              </Typography>
              <Typography sx={pSx}>
                <strong style={{ fontWeight: 600, color: 'inherit' }}>Website:</strong> https://auratap.app
              </Typography>
            </Box>

            <Typography sx={{ ...pSx, mt: 6, opacity: 0.65 }}>
              © 2026 AuraTap. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PrivacyPolicyModal;
